import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement,
  Title, Tooltip, Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';
import AppLayout from '../components/AppLayout';
import api from '../api/axiosInstance';
import './SectorRadar.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

// ── Helpers ───────────────────────────────────────────────────────────────────
function getISOWeek(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const y = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - y) / 86400000) + 1) / 7);
}

function todayMonth() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
}

function todayWeek() {
  const n = new Date();
  return `${n.getFullYear()}-W${String(getISOWeek(n)).padStart(2, '0')}`;
}

function todayDate() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
}

function formatCS(val) {
  const a = Math.abs(val);
  const sign = val < 0 ? '-' : '';
  if (a >= 1e7)  return sign + (a / 1e7).toFixed(2) + 'Cr';
  if (a >= 1e5)  return sign + (a / 1e5).toFixed(2) + 'L';
  if (a >= 1000) return sign + (a / 1000).toFixed(1) + 'K';
  return val.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

function formatCSFull(val) {
  return Math.round(val).toLocaleString('en-IN');
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function SectorRadar() {
  const [mode, setMode]           = useState('monthly');
  const [monthVal, setMonthVal]   = useState(todayMonth());
  const [weekVal, setWeekVal]     = useState(todayWeek());
  const [dateVal, setDateVal]     = useState(todayDate());

  const [allSectors, setAllSectors]     = useState([]);   // [{industry, count}]
  const [sectorSearch, setSectorSearch] = useState('');
  const [summaryData, setSummaryData]   = useState([]);   // [{sectorName, totalCS}]
  const [selectedSector, setSelectedSector] = useState('');
  const [sort, setSort]                 = useState('alpha');

  const [sectorSort, setSectorSort]         = useState('desc'); // 'desc' | 'asc' | 'alpha'
  const [stockSort, setStockSort]           = useState('desc'); // 'desc' | 'asc' | 'alpha'
  const [copiedStocks, setCopiedStocks]     = useState(false);

  const [stats, setStats] = useState(null); // {totalSectors, positive, negative, periodLabel}
  const [stocks, setStocks] = useState(null); // [{symbol, totalCS}] | null

  const [chartLoading, setChartLoading]   = useState(false);
  const [stockLoading, setStockLoading]   = useState(false);
  const [filterInfo, setFilterInfo]       = useState('Select a period and click Apply');
  const [filterOpen, setFilterOpen]       = useState(false);

  const sectorListRef = useRef(null);

  // ── Fetch sector list on mount ─────────────────────────────────────────────
  useEffect(() => {
    api.get('/sector-analysis/industries')
      .then(({ data }) => {
        if (data.success) {
          const sorted = (data.data || []).slice().sort((a, b) => a.industry.localeCompare(b.industry));
          setAllSectors(sorted);
        }
      })
      .catch(console.error);
  }, []);

  // ── Auto-apply on mount ────────────────────────────────────────────────────
  useEffect(() => {
    handleApply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Derived filtered + sorted sector list ───────────────────────────────────
  const filteredSectors = allSectors
    .filter(s => s.industry.toLowerCase().includes(sectorSearch.toLowerCase()))
    .slice()
    .sort((a, b) => {
      if (sectorSort === 'alpha') return a.industry.localeCompare(b.industry);
      const aCS = summaryData.find(d => d.sectorName === a.industry)?.totalCS ?? 0;
      const bCS = summaryData.find(d => d.sectorName === b.industry)?.totalCS ?? 0;
      return sectorSort === 'desc' ? bCS - aCS : aCS - bCS;
    });

  // ── Build API URL from current mode/date pickers ──────────────────────────
  function buildSummaryUrl() {
    if (mode === 'monthly') {
      const [y, m] = monthVal.split('-');
      return `/sector-analysis/monthly-summary?year=${y}&month=${parseInt(m)}`;
    }
    if (mode === 'weekly') {
      const [y, w] = weekVal.split('-W');
      return `/sector-analysis/weekly-summary?year=${y}&week=${parseInt(w)}`;
    }
    // daily
    return `/sector-analysis/daily-summary?date=${dateVal}`;
  }

  function buildPeriodLabel() {
    if (mode === 'monthly') {
      const [y, m] = monthVal.split('-');
      return new Date(parseInt(y), parseInt(m) - 1, 1)
        .toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    }
    if (mode === 'weekly') {
      const [y, w] = weekVal.split('-W');
      return `Week ${parseInt(w)}, ${y}`;
    }
    return new Date(dateVal + 'T00:00:00')
      .toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  }

  // ── Apply filter ──────────────────────────────────────────────────────────
  async function handleApply() {
    setChartLoading(true);
    setSummaryData([]);
    setStats(null);
    try {
      const url = buildSummaryUrl();
      const { data } = await api.get(url);
      if (data.success && data.data.length > 0) {
        const label = buildPeriodLabel();
        setSummaryData(data.data);
        const pos = data.data.filter(d => d.totalCS > 0).length;
        const neg = data.data.filter(d => d.totalCS < 0).length;
        setStats({ totalSectors: data.totalSectors, positive: pos, negative: neg, periodLabel: label });
        setFilterInfo(`${label} — ${data.totalSectors} sectors`);
        // Auto-select first sector if none selected
        const first = allSectors[0]?.industry;
        if (!selectedSector && first) {
          handleSelectSector(first, data.data);
        } else if (selectedSector) {
          loadStockBreakdown(selectedSector);
        }
      } else {
        setSummaryData([]);
        setStats(null);
        setFilterInfo('No data for selected period');
      }
    } catch (e) {
      console.error(e);
      setFilterInfo('Failed to load data');
    } finally {
      setChartLoading(false);
    }
  }

  // ── Reset ─────────────────────────────────────────────────────────────────
  function handleReset() {
    setMonthVal(todayMonth());
    setWeekVal(todayWeek());
    setDateVal(todayDate());
    setSelectedSector('');
    setStocks(null);
    handleApply();
  }

  // ── Mode change ───────────────────────────────────────────────────────────
  function handleModeChange(m) {
    setMode(m);
    setSummaryData([]);
    setStats(null);
    setSelectedSector('');
    setStocks(null);
    setFilterInfo('Select a period and click Apply');
  }

  // ── Select sector ─────────────────────────────────────────────────────────
  function handleSelectSector(name, data) {
    setSelectedSector(name);
    // Scroll into view
    setTimeout(() => {
      const el = sectorListRef.current?.querySelector(`[data-sector="${CSS.escape(name)}"]`);
      if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 0);
    loadStockBreakdown(name, data);
  }

  // ── Build stock breakdown query string ────────────────────────────────────
  function buildBreakdownQs(sectorName) {
    let qs = `industry=${encodeURIComponent(sectorName)}&limit=200`;
    if (mode === 'monthly') {
      const [y, m] = monthVal.split('-');
      qs += `&year=${y}&month=${parseInt(m)}`;
    } else if (mode === 'weekly') {
      const [y, w] = weekVal.split('-W');
      qs += `&year=${y}&week=${parseInt(w)}`;
    } else {
      qs += `&date=${dateVal}`;
    }
    return qs;
  }

  async function loadStockBreakdown(sectorName) {
    setStockLoading(true);
    setStocks(null);
    try {
      const qs = buildBreakdownQs(sectorName);
      const { data } = await api.get(`/sector-analysis/analysis?${qs}`);
      if (data.success && data.stockBreakdown.length > 0) {
        const sorted = data.stockBreakdown.slice().sort((a, b) => b.totalCS - a.totalCS);
        setStocks(sorted);
      } else {
        setStocks([]);
      }
    } catch {
      setStocks([]);
    } finally {
      setStockLoading(false);
    }
  }

  function copySymbolsForTV() {
    if (!stocks || stocks.length === 0) return;
    const text = stocks.map((s) => `NSE:${s.symbol}`).join(',');
    navigator.clipboard.writeText(text).catch(() => {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    });
    setCopiedStocks(true);
    setTimeout(() => setCopiedStocks(false), 2000);
  }

  // ── Chart data ────────────────────────────────────────────────────────────
  const chartData = (() => {
    if (!summaryData.length) return null;
    let sorted = summaryData.slice();
    if (sort === 'alpha') sorted.sort((a, b) => a.sectorName.localeCompare(b.sectorName));
    else sorted.sort((a, b) => b.totalCS - a.totalCS);

    const labels = sorted.map(d => d.sectorName);
    const values = sorted.map(d => d.totalCS);
    return {
      sortedData: sorted,
      chartJs: {
        labels,
        datasets: [{
          label: 'CS Value',
          data: values,
          backgroundColor: values.map(v => v >= 0 ? 'rgba(0,242,96,0.75)' : 'rgba(255,107,107,0.75)'),
          borderColor:     values.map(v => v >= 0 ? '#00f260' : '#ff6b6b'),
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
        }],
      },
    };
  })();

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 400 },
    onClick: (evt, elements) => {
      if (elements.length > 0 && chartData) {
        const name = chartData.sortedData[elements[0].index].sectorName;
        handleSelectSector(name);
      }
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(15,15,30,0.95)',
        borderColor: 'rgba(255,255,255,0.1)',
        borderWidth: 1,
        titleColor: '#fff',
        bodyColor: 'rgba(255,255,255,0.7)',
        callbacks: {
          label: ctx => ` CS: ${formatCS(ctx.raw)}`,
        },
      },
      datalabels: {
        display: ctx => Math.abs(ctx.dataset.data[ctx.dataIndex]) > 0,
        color: ctx => ctx.dataset.data[ctx.dataIndex] >= 0 ? '#00f260' : '#ff6b6b',
        anchor: ctx => ctx.dataset.data[ctx.dataIndex] >= 0 ? 'end' : 'start',
        align: ctx => ctx.dataset.data[ctx.dataIndex] >= 0 ? 'top' : 'bottom',
        font: { size: 9, weight: '700' },
        formatter: val => {
          const a = Math.abs(val);
          if (a >= 1e7) return (val / 1e7).toFixed(1) + 'Cr';
          if (a >= 1e5) return (val / 1e5).toFixed(1) + 'L';
          return val.toFixed(0);
        },
      },
    },
    scales: {
      x: {
        grid: { color: 'rgba(255,255,255,0.04)' },
        ticks: { color: 'rgba(255,255,255,0.55)', font: { size: 9 }, maxRotation: 45, minRotation: 30 },
        border: { color: 'rgba(255,255,255,0.1)' },
      },
      y: {
        grid: { color: 'rgba(255,255,255,0.05)' },
        ticks: {
          color: 'rgba(255,255,255,0.45)',
          font: { size: 9 },
          callback: v => {
            const a = Math.abs(v);
            if (a >= 1e7) return (v / 1e7).toFixed(1) + 'Cr';
            if (a >= 1e5) return (v / 1e5).toFixed(1) + 'L';
            return v;
          },
        },
        border: { color: 'rgba(255,255,255,0.1)' },
      },
    },
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <AppLayout pageBadge="Sector Radar">
      <div className={`sa-root${filterOpen ? ' filter-open' : ''}`}>

        {!filterOpen && (
          <button
            className="sa-filter-toggle-btn"
            onClick={() => setFilterOpen(true)}
            aria-label="Show filters"
          >
            <span>☰</span>
            <span>Filters</span>
          </button>
        )}

        {/* ── Filter Bar ─────────────────────────────────────────── */}
        <div className={`sa-filter-bar${filterOpen ? ' is-open' : ''}`}>
          {/* Mode toggle */}
          <div className="sa-mode-toggle sa-filter-mode">
            {['monthly', 'weekly', 'daily'].map(m => (
              <button
                key={m}
                className={`sa-mode-btn${mode === m ? ` active ${m}` : ''}`}
                onClick={() => handleModeChange(m)}
              >
                {m.charAt(0).toUpperCase() + m.slice(1)}
              </button>
            ))}
          </div>

          <div className="sa-filter-divider" />

          {/* Date picker */}
          <div className="sa-filter-group sa-filter-date">
            <span className="sa-filter-label">
              {mode === 'monthly' ? 'Month' : mode === 'weekly' ? 'Week' : 'Date'}
            </span>
            {mode === 'monthly' && (
              <input type="month" className="sa-filter-input" value={monthVal}
                onChange={e => setMonthVal(e.target.value)} />
            )}
            {mode === 'weekly' && (
              <input type="week" className="sa-filter-input" value={weekVal}
                onChange={e => setWeekVal(e.target.value)} />
            )}
            {mode === 'daily' && (
              <input type="date" className="sa-filter-input" value={dateVal}
                onChange={e => setDateVal(e.target.value)} />
            )}
          </div>

          <div className="sa-filter-divider" />

          <button className="sa-btn-apply sa-filter-apply" onClick={handleApply}>▶ Apply</button>
          <button className="sa-btn-reset sa-filter-reset" onClick={handleReset}>↺ Reset</button>

          <div className="sa-filter-divider" />

          <div className="sa-filter-info sa-filter-status">
            {filterInfo.includes('—')
              ? <><strong>{filterInfo.split('—')[0].trim()}</strong> — {filterInfo.split('—')[1]}</>
              : filterInfo}
          </div>

          <button
            type="button"
            className="sa-btn-apply sa-filter-close"
            onClick={() => setFilterOpen(false)}
            aria-label="Close filters"
          >
            ✕ Close Filter
          </button>
        </div>

        {/* ── 3-Column Body ──────────────────────────────────────── */}
        <div className="sa-body">

          {/* LEFT: Sector List */}
          <div className="sa-sector-panel">
            <div className="sa-sector-panel-header">
              <div className="sa-sector-panel-title">
                Sectors
                <span className="sa-sector-count-badge">{allSectors.length}</span>
              </div>
              <input
                type="text"
                className="sa-sector-search"
                placeholder="Search sector..."
                value={sectorSearch}
                onChange={e => setSectorSearch(e.target.value)}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6 }}>
                {[{ key: 'desc', label: '↓ CS' }, { key: 'asc', label: '↑ CS' }, { key: 'alpha', label: 'A–Z' }].map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setSectorSort(opt.key)}
                    style={{
                      fontSize: '0.65rem', fontWeight: 600, padding: '2px 7px',
                      borderRadius: 5, cursor: 'pointer', transition: 'all 0.15s',
                      border: sectorSort === opt.key ? '1px solid rgba(139,92,246,0.8)' : '1px solid rgba(255,255,255,0.12)',
                      background: sectorSort === opt.key ? 'rgba(139,92,246,0.25)' : 'rgba(255,255,255,0.05)',
                      color: sectorSort === opt.key ? '#c4b5fd' : 'rgba(255,255,255,0.45)',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="sa-sector-list" ref={sectorListRef}>
              {filteredSectors.length === 0 ? (
                <div className="sa-table-empty">
                  <div className="sa-table-empty-icon">⏳</div>
                  No sectors found
                </div>
              ) : filteredSectors.map(s => {
                const sd = summaryData.find(d => d.sectorName === s.industry);
                const csVal = sd ? sd.totalCS : null;
                const pillCls = csVal === null ? 'none' : csVal > 0 ? 'pos' : csVal < 0 ? 'neg' : 'none';
                const pillTxt = csVal !== null ? (csVal > 0 ? '+' : '') + formatCS(csVal) : null;
                return (
                  <div
                    key={s.industry}
                    data-sector={s.industry}
                    className={`sa-sector-item${selectedSector === s.industry ? ' selected' : ''}`}
                    onClick={() => handleSelectSector(s.industry)}
                  >
                    <div className="sa-sector-name">{s.industry}</div>
                    <div className="sa-sector-meta">
                      <span className="sa-sector-stock-count">{s.count} stocks</span>
                      {pillTxt && <span className={`sa-cs-pill ${pillCls}`}>{pillTxt}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* CENTER: Chart + Stats */}
          <div className="sa-chart-panel">
            {/* Summary tiles */}
            {stats && (
              <div className="sa-summary-row">
                <div className="sa-stat-tile t1">
                  <div className="sa-stat-label">Total Sectors</div>
                  <div className="sa-stat-value">{stats.totalSectors}</div>
                </div>
                <div className="sa-stat-tile t2">
                  <div className="sa-stat-label">Positive Flow</div>
                  <div className="sa-stat-value">{stats.positive}</div>
                </div>
                <div className="sa-stat-tile t3">
                  <div className="sa-stat-label">Negative Flow</div>
                  <div className="sa-stat-value">{stats.negative}</div>
                </div>
                <div className="sa-stat-tile t4">
                  <div className="sa-stat-label">Period</div>
                  <div className="sa-stat-value small">{stats.periodLabel}</div>
                </div>
              </div>
            )}

            {/* Bar chart */}
            <div className="sa-chart-card">
              <div className="sa-chart-header">
                <div className="sa-chart-title">
                  CS Value by Sector —{' '}
                  <span>{stats ? stats.periodLabel : 'select a period'}</span>
                </div>
                <div className="sa-sort-toggle">
                  <button
                    className={`sa-sort-btn${sort === 'alpha' ? ' active' : ''}`}
                    onClick={() => setSort('alpha')}
                  >A–Z</button>
                  <button
                    className={`sa-sort-btn${sort === 'value' ? ' active' : ''}`}
                    onClick={() => setSort('value')}
                  >By Value</button>
                </div>
              </div>
              <div className="sa-chart-body">
                {chartLoading && (
                  <div className="sa-chart-loading">
                    <div className="sa-spinner" />
                    Loading…
                  </div>
                )}
                {!chartLoading && !chartData && (
                  <div className="sa-chart-placeholder">
                    <div className="sa-ph-icon">📊</div>
                    <div>Select a period and click Apply to load chart</div>
                  </div>
                )}
                {!chartLoading && chartData && (
                  <Bar data={chartData.chartJs} options={chartOptions} />
                )}
              </div>
            </div>
          </div>

          {/* RIGHT: Stock Breakdown */}
          <div className="sa-table-panel">
            <div className="sa-table-panel-header">
              <div className="sa-table-panel-title">Stock Breakdown</div>
              <div className="sa-table-panel-sector">
                {selectedSector || '— select a sector —'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6 }}>
                <button
                  onClick={copySymbolsForTV}
                  disabled={!stocks || stocks.length === 0}
                  title="Copy all symbols for TradingView watchlist"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    fontSize: '0.65rem', fontWeight: 600,
                    padding: '2px 8px', borderRadius: 6, cursor: 'pointer',
                    border: copiedStocks ? '1px solid #10b981' : '1px solid rgba(16,185,129,0.4)',
                    background: copiedStocks ? 'rgba(16,185,129,0.25)' : 'rgba(16,185,129,0.1)',
                    color: copiedStocks ? '#6ee7b7' : '#10b981',
                    transition: 'all 0.2s',
                    opacity: stocks && stocks.length > 0 ? 1 : 0.4,
                  }}
                >
                  {copiedStocks ? '✓ Copied!' : '⧉ Copy'}
                </button>
                {[{ key: 'desc', label: '↓ CS' }, { key: 'asc', label: '↑ CS' }, { key: 'alpha', label: 'A–Z' }].map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setStockSort(opt.key)}
                    style={{
                      fontSize: '0.65rem', fontWeight: 600, padding: '2px 7px',
                      borderRadius: 5, cursor: 'pointer', transition: 'all 0.15s',
                      border: stockSort === opt.key ? '1px solid rgba(139,92,246,0.8)' : '1px solid rgba(255,255,255,0.12)',
                      background: stockSort === opt.key ? 'rgba(139,92,246,0.25)' : 'rgba(255,255,255,0.05)',
                      color: stockSort === opt.key ? '#c4b5fd' : 'rgba(255,255,255,0.45)',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="sa-table-scroll">
              <table className="sa-stocks-table">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th className="right">Total CS</th>
                  </tr>
                </thead>
                <tbody>
                  {stockLoading ? (
                    <tr>
                      <td colSpan={2}>
                        <div className="sa-table-empty">
                          <div className="sa-spinner" style={{ margin: '0 auto 6px' }} />
                          Loading…
                        </div>
                      </td>
                    </tr>
                  ) : stocks === null ? (
                    <tr>
                      <td colSpan={2}>
                        <div className="sa-table-empty">
                          <div className="sa-table-empty-icon">👈</div>
                          Click a sector
                        </div>
                      </td>
                    </tr>
                  ) : stocks.length === 0 ? (
                    <tr>
                      <td colSpan={2}>
                        <div className="sa-table-empty">
                          <div className="sa-table-empty-icon">📭</div>
                          No data for this period
                        </div>
                      </td>
                    </tr>
                  ) : (stocks.slice().sort((a, b) => {
                      if (stockSort === 'alpha') return a.symbol.localeCompare(b.symbol);
                      return stockSort === 'desc' ? b.totalCS - a.totalCS : a.totalCS - b.totalCS;
                    })).map((s, i) => {
                      let calY, calM;
                      if (mode === 'monthly') {
                        const [y, m] = monthVal.split('-');
                        calY = y; calM = parseInt(m);
                      } else if (mode === 'daily') {
                        const [y, m] = dateVal.split('-');
                        calY = y; calM = parseInt(m);
                      } else {
                        const now = new Date();
                        calY = now.getFullYear();
                        calM = now.getMonth() + 1;
                      }
                      const calUrl = `/cs-calendar?symbol=${encodeURIComponent(s.symbol)}&year=${calY}&month=${calM}&collection=${(() => {
                        const cats = s.categories || (s.category ? [s.category] : []);
                        if (cats.some(c => c === 'N50' || c === 'nifty50')) return 'nifty50';
                        if (cats.some(c => c === 'Others' || c === 'others')) return 'cash';
                        return 'fo';
                      })()}`;
                      return (
                    <tr key={i}>
                      <td className="sa-td-symbol">
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {s.symbol}
                          <a
                            href={`https://in.tradingview.com/chart/?symbol=NSE%3A${s.symbol}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={`View ${s.symbol} on TradingView`}
                            style={{ color: '#60a5fa', lineHeight: 1, flexShrink: 0 }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                            </svg>
                          </a>
                          <a
                            href={calUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={`View ${s.symbol} CS Calendar`}
                            style={{ color: '#a78bfa', lineHeight: 1, flexShrink: 0 }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                              <line x1="16" y1="2" x2="16" y2="6" />
                              <line x1="8" y1="2" x2="8" y2="6" />
                              <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                          </a>
                        </span>
                      </td>
                      <td className={`right ${s.totalCS >= 0 ? 'sa-td-pos' : 'sa-td-neg'}`}>
                        {formatCSFull(s.totalCS)}
                      </td>
                    </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    </AppLayout>
  );
}
